import { useCallback, useEffect, useState } from 'react';
import { PageHeader, EmptyState, TableSkeleton } from '../components/common/PageHeader';
import { SearchFilterBar } from '../components/common/SearchFilterBar';
import { PaginationControls } from '../components/common/PaginationControls';
import { RoleBadge } from '../components/common/StatusBadge';
import { listUsers } from '../services/userService';
import { getErrorMessage } from '../services/apiClient';
import { useToast } from '../context/ToastContext';
import { formatDate } from '../utils/format';

export default function UsersPage() {
  const { showToast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (roleFilter !== 'all') params.role = roleFilter;
      const res = await listUsers(params);
      setUsers(res.data || []);
      setMeta(res.meta || { total: 0, totalPages: 1 });
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, roleFilter, showToast]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const filtered = users.filter((user) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return user.name.toLowerCase().includes(q) || user.email.toLowerCase().includes(q);
  });

  return (
    <div>
      <PageHeader title="Users" description="View registered customers and admins" />

      <div className="card">
        <SearchFilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by name or email..."
          filters={[{
            key: 'role',
            label: 'Roles',
            options: [
              { value: 'admin', label: 'Admin' },
              { value: 'customer', label: 'Customer' },
            ],
          }]}
          filterValues={{ role: roleFilter }}
          onFilterChange={(_, value) => { setRoleFilter(value); setPage(1); }}
          onClear={() => { setSearch(''); setRoleFilter('all'); setPage(1); }}
        />

        {loading ? (
          <TableSkeleton cols={4} />
        ) : filtered.length === 0 ? (
          <EmptyState title="No users found" />
        ) : (
          <>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((user) => (
                    <tr key={user._id}>
                      <td>
                        <div className="cell-product">
                          <div className="user-avatar sm">{user.name.charAt(0)}</div>
                          <span className="cell-title">{user.name}</span>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td><RoleBadge role={user.role} /></td>
                      <td>{formatDate(user.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls page={page} totalPages={meta.totalPages} totalItems={meta.total} onPageChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
}
